import { ArrowRight } from "lucide-react";
import { Counter } from "@/components/effects";
import SystemArchitecture from "@/components/sections/SystemArchitecture";

/* Right-side fan — central "Your app" with arcs to 5 channels */
const FAN_TARGETS = [
  {
    name: "iMessage",
    y: 36,
    img: "/logos/imessage.png",
  },
  {
    name: "SMS / RCS",
    y: 108,
    img: "/logos/sms-rcs.png",
  },
  {
    name: "WhatsApp",
    y: 180,
    img: "/logos/whatsapp.png",
  },
  {
    name: "Telegram",
    y: 252,
    img: "/logos/telegram.png",
  },
  {
    name: "Slack",
    y: 324,
    img: "/logos/slack.png",
  },
];

function OldWayGraphic() {
  return (
    <svg viewBox="0 0 420 320" className="w-full max-w-md mx-auto" role="img" aria-label="A retro Safari browser and old iPhone with colorful app icons">
      <defs>
        {/* Safari titlebar metallic gradient */}
        <linearGradient id="safari-bar" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f7f7f7" />
          <stop offset="50%" stopColor="#dcdcdc" />
          <stop offset="100%" stopColor="#bdbdbd" />
        </linearGradient>
        <linearGradient id="addr-bar" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#e6e6e6" />
        </linearGradient>
        {/* Phone body gradient (old iPhone aluminum) */}
        <linearGradient id="phone-body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3a3a3c" />
          <stop offset="100%" stopColor="#1c1c1e" />
        </linearGradient>
        {/* Traffic-light dot highlights */}
        <radialGradient id="tl-red" cx="0.35" cy="0.35" r="0.7">
          <stop offset="0%" stopColor="#ff7b6b" />
          <stop offset="100%" stopColor="#e54336" />
        </radialGradient>
        <radialGradient id="tl-yellow" cx="0.35" cy="0.35" r="0.7">
          <stop offset="0%" stopColor="#ffe27a" />
          <stop offset="100%" stopColor="#f1bd2e" />
        </radialGradient>
        <radialGradient id="tl-green" cx="0.35" cy="0.35" r="0.7">
          <stop offset="0%" stopColor="#86eb7f" />
          <stop offset="100%" stopColor="#2bbf3f" />
        </radialGradient>
        {/* App gradients */}
        <linearGradient id="ic-phone" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7dd87a" />
          <stop offset="100%" stopColor="#2eaa3f" />
        </linearGradient>
        <linearGradient id="ic-msg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8ee37c" />
          <stop offset="100%" stopColor="#3fc14a" />
        </linearGradient>
        <linearGradient id="ic-mail" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8ecbff" />
          <stop offset="100%" stopColor="#1e7ee0" />
        </linearGradient>
        <linearGradient id="ic-safari" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#a9d8ff" />
          <stop offset="100%" stopColor="#1f73c2" />
        </linearGradient>
        <linearGradient id="ic-photos" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fff7d6" />
          <stop offset="100%" stopColor="#e9e9e9" />
        </linearGradient>
        <linearGradient id="ic-camera" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#bcbcbc" />
          <stop offset="100%" stopColor="#5e5e5e" />
        </linearGradient>
        <linearGradient id="ic-music" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ff8aa0" />
          <stop offset="100%" stopColor="#d11a3d" />
        </linearGradient>
        <linearGradient id="ic-notes" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffe98a" />
          <stop offset="100%" stopColor="#e2b300" />
        </linearGradient>
        <linearGradient id="ic-maps" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e7d6a2" />
          <stop offset="100%" stopColor="#a78f60" />
        </linearGradient>
        <linearGradient id="ic-cal" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#dcdcdc" />
        </linearGradient>
        <linearGradient id="ic-settings" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#cfcfcf" />
          <stop offset="100%" stopColor="#777777" />
        </linearGradient>
        <linearGradient id="ic-weather" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#86b6f1" />
          <stop offset="100%" stopColor="#2e6ec8" />
        </linearGradient>
        <linearGradient id="ic-stocks" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#444" />
          <stop offset="100%" stopColor="#111" />
        </linearGradient>
        <linearGradient id="ic-calc" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3a3a3a" />
          <stop offset="100%" stopColor="#0a0a0a" />
        </linearGradient>
        <linearGradient id="ic-clock" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2a2a2a" />
          <stop offset="100%" stopColor="#000000" />
        </linearGradient>
        <linearGradient id="screen-wp" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0a1a3a" />
          <stop offset="100%" stopColor="#040814" />
        </linearGradient>
      </defs>

      {/* ============ Safari window ============ */}
      <g transform="translate(10, 40)">
        {/* shadow */}
        <rect x="2" y="4" width="240" height="170" fill="#000" opacity="0.4" />
        {/* window body */}
        <rect x="0" y="0" width="240" height="170" fill="#f4f4f4" stroke="#9a9a9a" strokeWidth="0.8" />
        {/* titlebar */}
        <rect x="0" y="0" width="240" height="22" fill="url(#safari-bar)" stroke="#8e8e8e" strokeWidth="0.8" />
        {/* traffic lights */}
        <circle cx="11" cy="11" r="5" fill="url(#tl-red)" stroke="#9a2a22" strokeWidth="0.6" />
        <circle cx="25" cy="11" r="5" fill="url(#tl-yellow)" stroke="#9a7a1a" strokeWidth="0.6" />
        <circle cx="39" cy="11" r="5" fill="url(#tl-green)" stroke="#2a7a32" strokeWidth="0.6" />
        {/* title text */}
        <text x="120" y="15" textAnchor="middle" fontFamily="-apple-system, Helvetica, sans-serif" fontSize="9" fill="#3a3a3a">Your App — Safari</text>

        {/* toolbar */}
        <rect x="0" y="22" width="240" height="20" fill="#ececec" stroke="#bcbcbc" strokeWidth="0.5" />
        {/* back/forward */}
        <polygon points="10,32 16,28 16,36" fill="#7a7a7a" />
        <polygon points="22,28 22,36 28,32" fill="#bdbdbd" />
        {/* address bar */}
        <rect x="36" y="26" width="160" height="12" rx="2" fill="url(#addr-bar)" stroke="#a8a8a8" strokeWidth="0.5" />
        <circle cx="44" cy="32" r="2" fill="none" stroke="#6a6a6a" strokeWidth="0.8" />
        <line x1="45.5" y1="33.5" x2="48" y2="36" stroke="#6a6a6a" strokeWidth="0.8" />
        <text x="52" y="35" fontFamily="-apple-system, Helvetica, sans-serif" fontSize="8" fill="#5a5a5a">https://yourapp.com</text>
        {/* reload */}
        <circle cx="206" cy="32" r="4" fill="none" stroke="#7a7a7a" strokeWidth="0.8" />
        <polyline points="208,28 210,32 206,30" fill="none" stroke="#7a7a7a" strokeWidth="0.8" />

        {/* page content */}
        <rect x="14" y="50" width="60" height="14" fill="#1f73c2" />
        <text x="44" y="60" textAnchor="middle" fontFamily="-apple-system, sans-serif" fontSize="7" fill="#fff">VISIT US</text>

        <text x="14" y="78" fontFamily="Georgia, serif" fontSize="11" fontWeight="bold" fill="#222">Welcome to Your App</text>
        <line x1="14" y1="86" x2="220" y2="86" stroke="#cccccc" strokeWidth="0.6" />
        <line x1="14" y1="94" x2="220" y2="94" stroke="#cccccc" strokeWidth="0.6" />
        <line x1="14" y1="102" x2="180" y2="102" stroke="#cccccc" strokeWidth="0.6" />
        <line x1="14" y1="110" x2="210" y2="110" stroke="#cccccc" strokeWidth="0.6" />

        {/* hero image placeholder */}
        <rect x="14" y="120" width="100" height="40" fill="#e8d49a" stroke="#b89a55" strokeWidth="0.6" />
        <circle cx="34" cy="135" r="5" fill="#f0c040" />
        <polygon points="50,148 70,128 90,148" fill="#a8c47c" stroke="#7a9658" strokeWidth="0.5" />
        {/* sidebar text */}
        <line x1="124" y1="124" x2="220" y2="124" stroke="#cccccc" strokeWidth="0.6" />
        <line x1="124" y1="132" x2="220" y2="132" stroke="#cccccc" strokeWidth="0.6" />
        <line x1="124" y1="140" x2="200" y2="140" stroke="#cccccc" strokeWidth="0.6" />
        <line x1="124" y1="148" x2="220" y2="148" stroke="#cccccc" strokeWidth="0.6" />
        <line x1="124" y1="156" x2="180" y2="156" stroke="#cccccc" strokeWidth="0.6" />
      </g>

      {/* ============ Old iPhone ============ */}
      <g transform="translate(280, 18)">
        {/* shadow */}
        <rect x="3" y="6" width="120" height="220" rx="18" fill="#000" opacity="0.45" />
        {/* body */}
        <rect x="0" y="0" width="120" height="220" rx="18" fill="url(#phone-body)" stroke="#0a0a0a" strokeWidth="1" />
        {/* bezel inner */}
        <rect x="3" y="3" width="114" height="214" rx="16" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.8" />
        {/* speaker */}
        <rect x="48" y="9" width="24" height="3" rx="1.5" fill="#0a0a0a" stroke="rgba(255,255,255,0.08)" strokeWidth="0.4" />
        {/* front camera */}
        <circle cx="60" cy="18" r="1.8" fill="#0a0a0a" stroke="rgba(255,255,255,0.1)" strokeWidth="0.4" />
        {/* screen */}
        <rect x="6" y="24" width="108" height="170" fill="url(#screen-wp)" />
        {/* status bar */}
        <rect x="6" y="24" width="108" height="10" fill="rgba(0,0,0,0.35)" />
        <text x="12" y="32" fontFamily="-apple-system, Helvetica, sans-serif" fontSize="6" fill="#fff">●●●●● Carrier</text>
        <text x="60" y="32" textAnchor="middle" fontFamily="-apple-system, Helvetica, sans-serif" fontSize="6" fill="#fff" fontWeight="bold">9:41 AM</text>
        <rect x="98" y="28" width="12" height="4" rx="0.6" fill="none" stroke="#fff" strokeWidth="0.5" />
        <rect x="99" y="29" width="9" height="2" fill="#fff" />

        {/* App grid 4x5 */}
        {(() => {
          const apps = [
            { fill: "url(#ic-msg)", glyph: "msg" },
            { fill: "url(#ic-cal)", glyph: "cal" },
            { fill: "url(#ic-photos)", glyph: "photos" },
            { fill: "url(#ic-camera)", glyph: "cam" },
            { fill: "url(#ic-weather)", glyph: "wx" },
            { fill: "url(#ic-clock)", glyph: "clock" },
            { fill: "url(#ic-maps)", glyph: "maps" },
            { fill: "url(#ic-notes)", glyph: "notes" },
            { fill: "url(#ic-stocks)", glyph: "stocks" },
            { fill: "url(#ic-settings)", glyph: "gear" },
            { fill: "url(#ic-calc)", glyph: "calc" },
            { fill: "url(#ic-mail)", glyph: "mail" },
          ];
          return apps.map((a, i) => {
            const col = i % 4;
            const row = Math.floor(i / 4);
            const x = 10 + col * 25;
            const y = 40 + row * 32;
            return (
              <g key={i} transform={`translate(${x}, ${y})`}>
                <rect width="20" height="20" rx="4.5" fill={a.fill} stroke="rgba(0,0,0,0.5)" strokeWidth="0.4" />
                {/* gloss */}
                <path d="M1.5 1.5 Q10 8 18.5 1.5 L18.5 9 Q10 13 1.5 9 Z" fill="rgba(255,255,255,0.18)" />
                {/* tiny glyph hints */}
                {a.glyph === "msg" && (
                  <ellipse cx="10" cy="10" rx="6" ry="4" fill="#fff" />
                )}
                {a.glyph === "cal" && (
                  <>
                    <text x="10" y="9" textAnchor="middle" fontSize="3" fill="#d83a3a" fontFamily="Helvetica" fontWeight="bold">SAT</text>
                    <text x="10" y="16" textAnchor="middle" fontSize="7" fill="#222" fontFamily="Helvetica" fontWeight="bold">19</text>
                  </>
                )}
                {a.glyph === "photos" && (
                  <g transform="translate(10,10)">
                    {[0, 60, 120, 180, 240, 300].map((deg) => (
                      <ellipse key={deg} cx="0" cy="-4" rx="2" ry="4" fill={["#f7b500", "#e53935", "#43a047", "#1e88e5", "#8e24aa", "#fb8c00"][deg / 60]} transform={`rotate(${deg})`} opacity="0.9" />
                    ))}
                  </g>
                )}
                {a.glyph === "cam" && (
                  <>
                    <rect x="3" y="6" width="14" height="9" rx="1.5" fill="#1a1a1a" />
                    <circle cx="10" cy="10.5" r="2.5" fill="none" stroke="#bbb" strokeWidth="0.8" />
                  </>
                )}
                {a.glyph === "wx" && (
                  <>
                    <circle cx="13" cy="8" r="3" fill="#ffd84d" />
                    <ellipse cx="9" cy="13" rx="6" ry="3" fill="#fff" />
                  </>
                )}
                {a.glyph === "clock" && (
                  <>
                    <circle cx="10" cy="10" r="6" fill="#fff" stroke="#000" strokeWidth="0.5" />
                    <line x1="10" y1="10" x2="10" y2="5.5" stroke="#000" strokeWidth="0.6" />
                    <line x1="10" y1="10" x2="13" y2="10" stroke="#e53935" strokeWidth="0.6" />
                  </>
                )}
                {a.glyph === "maps" && (
                  <>
                    <path d="M3 14 L8 5 L13 9 L17 6 L17 16 L3 16 Z" fill="#8aa86b" />
                    <line x1="3" y1="14" x2="17" y2="11" stroke="#fff" strokeWidth="0.4" strokeDasharray="1 1" />
                  </>
                )}
                {a.glyph === "notes" && (
                  <>
                    <line x1="4" y1="7" x2="16" y2="7" stroke="#a86a00" strokeWidth="0.5" />
                    <line x1="4" y1="10" x2="16" y2="10" stroke="#a86a00" strokeWidth="0.5" />
                    <line x1="4" y1="13" x2="14" y2="13" stroke="#a86a00" strokeWidth="0.5" />
                  </>
                )}
                {a.glyph === "stocks" && (
                  <>
                    <polyline points="3,15 7,11 11,13 17,5" fill="none" stroke="#7bd17b" strokeWidth="1" />
                  </>
                )}
                {a.glyph === "gear" && (
                  <>
                    <circle cx="10" cy="10" r="4" fill="none" stroke="#fff" strokeWidth="0.7" />
                    <circle cx="10" cy="10" r="1.6" fill="#fff" />
                  </>
                )}
                {a.glyph === "calc" && (
                  <>
                    <rect x="4" y="5" width="12" height="3" fill="#222" stroke="#555" strokeWidth="0.3" />
                    <rect x="4" y="10" width="2.5" height="2" fill="#888" />
                    <rect x="8" y="10" width="2.5" height="2" fill="#888" />
                    <rect x="12" y="10" width="2.5" height="2" fill="#f5a623" />
                    <rect x="4" y="13" width="2.5" height="2" fill="#888" />
                    <rect x="8" y="13" width="2.5" height="2" fill="#888" />
                    <rect x="12" y="13" width="2.5" height="2" fill="#f5a623" />
                  </>
                )}
                {a.glyph === "mail" && (
                  <>
                    <rect x="3" y="6" width="14" height="9" rx="0.6" fill="#fff" stroke="#1c5fa8" strokeWidth="0.5" />
                    <polyline points="3,6 10,12 17,6" fill="none" stroke="#1c5fa8" strokeWidth="0.5" />
                  </>
                )}
              </g>
            );
          });
        })()}

        {/* dock background */}
        <rect x="6" y="170" width="108" height="24" fill="rgba(255,255,255,0.06)" />
        {/* home button */}
        <circle cx="60" cy="207" r="7" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="0.8" />
        <rect x="56" y="203" width="8" height="8" rx="1.2" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.6" />
      </g>

      {/* Faded "forgotten" labels (kept) */}
      <g transform="translate(10, 268)">
        <text x="0" y="0" fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="3" fill="rgba(255,255,255,0.25)">
          LAST OPENED · 47 DAYS AGO
        </text>
        <line x1="0" y1="10" x2="240" y2="10" stroke="rgba(255,255,255,0.1)" strokeDasharray="2 4" />
      </g>
      <g transform="translate(10, 296)">
        <text x="0" y="0" fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="3" fill="rgba(255,255,255,0.25)">
          INBOX · PROMOTIONS · UNREAD (138)
        </text>
        <line x1="0" y1="10" x2="320" y2="10" stroke="rgba(255,255,255,0.1)" strokeDasharray="2 4" />
      </g>
    </svg>
  );
}

function NewWayGraphic() {
  // viewBox 480 x 360, center node at (60, 180), endpoints at x=320
  const cx = 70;
  const cy = 180;
  const tx = 320; // icon center x
  const VB_W = 480;
  const VB_H = 360;
  const KEY_WHITE = new Set(["iMessage", "WhatsApp", "Telegram"]);
  return (
    <div className="relative w-full max-w-md mx-auto">
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} className="w-full block" role="img" aria-label="Your app connecting instantly to messaging channels">
        {/* arcs */}
        {FAN_TARGETS.map((t, i) => {
          const ty = t.y;
          const ctrlX = (cx + tx) / 2 + 40;
          const ctrlY = (cy + ty) / 2;
          return (
            <g key={t.name}>
              <path
                d={`M ${cx + 30} ${cy} Q ${ctrlX} ${ctrlY} ${tx - 28} ${ty}`}
                stroke="#FACC15"
                strokeWidth="1.2"
                fill="none"
                strokeOpacity={0.95 - i * 0.08}
              />
              {/* arrow head */}
              <path
                d={`M ${tx - 28} ${ty} l -7 -3.5 l 0 7 z`}
                fill="#FACC15"
                fillOpacity={0.95 - i * 0.08}
              />
            </g>
          );
        })}

        {/* central "Your app" node */}
        <g>
          <rect x={cx - 40} y={cy - 30} width="80" height="60" fill="#FACC15" />
          <text
            x={cx}
            y={cy - 6}
            textAnchor="middle"
            fontFamily="Outfit, sans-serif"
            fontSize="13"
            fontWeight="500"
            fill="#050505"
            letterSpacing="0.4"
          >
            YOUR
          </text>
          <text
            x={cx}
            y={cy + 12}
            textAnchor="middle"
            fontFamily="Outfit, sans-serif"
            fontSize="13"
            fontWeight="500"
            fill="#050505"
            letterSpacing="0.4"
          >
            APP
          </text>
          <rect
            x={cx - 46}
            y={cy - 36}
            width="92"
            height="72"
            fill="none"
            stroke="#FACC15"
            strokeOpacity="0.35"
            className="animate-breathe"
          />
        </g>
      </svg>

      {/* HTML overlay for logos + labels — more reliable than SVG <image> across browsers */}
      <div className="absolute inset-0 pointer-events-none">
        {FAN_TARGETS.map((t) => {
          const leftPct = (tx / VB_W) * 100;
          const topPct = (t.y / VB_H) * 100;
          const keyed = KEY_WHITE.has(t.name);
          return (
            <div
              key={t.name}
              className="absolute flex items-center gap-2"
              style={{
                left: `${leftPct}%`,
                top: `${topPct}%`,
                transform: "translate(-50%, -50%)",
              }}
            >
              <img
                src={t.img}
                alt=""
                loading="lazy"
                className="w-8 h-8 sm:w-9 sm:h-9 object-contain"
              />
              <span className="font-heading text-white text-sm sm:text-base font-light whitespace-nowrap">
                {t.name}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Platforms() {
  return (
    <section
      id="platforms"
      data-testid="platforms-section"
      className="relative py-24 md:py-32 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-12">
          <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
            §04 · Old Way vs. New Way
          </span>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-end mb-20">
          <h2 className="lg:col-span-8 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light leading-snug">
            Stop building destinations.
            <br />
            <span className="text-zinc-500">Start having conversations.</span>
          </h2>
          <p className="lg:col-span-4 text-base leading-relaxed text-zinc-400 font-light">
            Software wins when it shows up where attention already lives.
            Compare the two paths your customers will actually experience.
          </p>
        </div>

        {/* Comparison */}
        <div
          data-testid="comparison-graphic"
          className="grid lg:grid-cols-2 gap-px bg-white/10 border border-white/10 relative"
        >
          {/* center "VS" badge */}
          <div className="hidden lg:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="w-12 h-12 bg-[#050505] border border-white/20 flex items-center justify-center">
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-yellow-400">vs</span>
            </div>
          </div>

          {/* LEFT — The Old Way */}
          <div
            data-testid="old-way-card"
            className="bg-[#050505] p-8 md:p-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-2 h-2 bg-zinc-500" />
              <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
                Before · standard
              </span>
            </div>

            <h3 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white mb-2">
              The Old Way
            </h3>
            <p className="text-base text-zinc-500 font-light mb-8">
              Forgotten websites. Buried apps.
            </p>

            <div className="mb-10">
              <OldWayGraphic />
            </div>

            <p className="text-base leading-relaxed text-zinc-400 font-light max-w-md">
              If your app lives on a website, people have to remember to visit
              it. Native apps are the same — buried two screens deep and rarely
              updated. After launch, your app is forgotten. And your email to
              them lands in <span className="text-zinc-300">&quot;Promotions.&quot;</span>
            </p>

            <div className="mt-10 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
              {[
                { v: <Counter value={20} suffix="%" />, k: "Email open rate", pre: "~" },
                { v: <Counter value={2} suffix="%" />, k: "Email click rate", pre: "~" },
                { v: <Counter value={70} suffix="%" />, k: "Apps abandoned in 30d" },
              ].map((s) => (
                <div key={s.k} className="bg-[#050505] p-4">
                  <div className="font-heading text-xl font-light text-zinc-300">
                    {s.pre || ""}{s.v}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-600 mt-1 font-mono">
                    {s.k}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — The New Way */}
          <div
            data-testid="new-way-card"
            className="bg-[#050505] p-8 md:p-12 ring-1 ring-yellow-400/30"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-2 h-2 bg-yellow-400" />
              <span className="text-xs uppercase tracking-[0.22em] text-yellow-400 font-mono">
                Now · invisible
              </span>
            </div>

            <h3 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white mb-2">
              The New Way
            </h3>
            <p className="text-base text-zinc-500 font-light mb-8">
              Meet people where they're at. Instantly.
            </p>

            <div className="mb-10">
              <NewWayGraphic />
            </div>

            <p className="text-base leading-relaxed text-zinc-400 font-light max-w-md">
              iMessage has a <span className="text-yellow-400">98% open rate</span>{" "}
              with <span className="text-yellow-400">~30% conversions</span>. If
              your customers, audience, followers, fans — or even employees —
              use instant messages, you already have a direct, trusted channel
              to them. Use it.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-px bg-white/10 border border-yellow-400/30">
              {[
                { v: <Counter value={98} suffix="%" />, k: "iMessage open rate" },
                { v: <><span>~</span><Counter value={30} suffix="%" /></>, k: "Conversion" },
                { v: <><span>&lt;</span><Counter value={10} suffix=" seconds" /></>, k: "Median reply time" },
              ].map((s, i) => (
                <div key={i} className="bg-[#050505] p-4">
                  <div className="font-heading text-xl font-light text-yellow-400">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-1 font-mono">
                    {s.k}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Under the hood — system architecture diagram */}
        <SystemArchitecture />

        {/* Bottom unify strip */}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border border-white/10 px-6 py-4 bg-white/[0.02]">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 bg-green-400 animate-pulse" />
            <span className="font-mono text-xs uppercase tracking-[0.22em] text-zinc-400">
              One unified system · routed across every channel your audience already uses
            </span>
          </div>
          <a
            href="#pricing"
            data-testid="platforms-cta"
            className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            See plans
            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" strokeWidth={2} />
          </a>
        </div>
      </div>
    </section>
  );
}
